import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

const ITEMS_PER_PAGE = 6;

export const useDocuments = (filters = {}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { search = '', status = 'all' } = filters;

  const {
    data,
    isLoading: loading,
    isFetchingNextPage: loadingMore,
    hasNextPage: hasMore,
    fetchNextPage: loadMoreDocuments,
    error,
  } = useInfiniteQuery({
    queryKey: ['documents', user?.id, search, status],
    queryFn: async ({ pageParam = 0 }) => {
      if (!user) return { freshData: [], count: 0 };

      const from = pageParam * ITEMS_PER_PAGE;
      const to = from + ITEMS_PER_PAGE - 1;

      let query = supabase
        .from('documents')
        .select('*', { count: 'exact' })
        .eq('user_id', user.id);

      if (status !== 'all') {
        query = query.eq('processing_status', status);
      }

      if (search.trim()) {
        query = query.or(`file_name.ilike.%${search}%,summary.ilike.%${search}%`);
      }

      const { data: dbData, error: fetchError, count } = await query
        .order('created_at', { ascending: false })
        .range(from, to);

      if (fetchError) throw fetchError;

      return {
        freshData: dbData || [],
        count: count || 0,
        nextPage: pageParam + 1,
      };
    },
    enabled: !!user,
    getNextPageParam: (lastPage, allPages) => {
      const loadedAmount = allPages.reduce((sum, page) => sum + page.freshData.length, 0);
      return loadedAmount < lastPage.count ? lastPage.nextPage : undefined;
    },
    staleTime: 1000 * 60 * 5,
  });

  const documents = data ? data.pages.flatMap((page) => page.freshData) : [];

  const refreshDocuments = () => {
    queryClient.invalidateQueries({ queryKey: ['documents', user?.id] });
  };

  // ── FIX: getDocument was missing — DocumentView.jsx calls this on every load
  const getDocument = async (id) => {
    if (!user) return null;

    // Check TanStack cache first — avoids a network round-trip if the
    // document was already loaded in the history list
    const cached = documents.find((d) => d.id === id);
    if (cached) return cached;

    // Not in cache (direct URL navigation or page refresh) — fetch from DB
    const { data: docData, error: fetchError } = await supabase
      .from('documents')
      .select('*')
      .eq('id', id)
      .eq('user_id', user.id)  // enforces ownership client-side too
      .single();

    if (fetchError || !docData) return null;
    return docData;
  };

  const deleteMutation = useMutation({
    mutationFn: async ({ documentId, filePath }) => {
      let pathToDelete = filePath;

      if (!pathToDelete) {
        const { data: docData } = await supabase
          .from('documents')
          .select('file_url')
          .eq('id', documentId)
          .single();
        if (docData?.file_url && docData.file_url.includes('/storage/v1/object/public/documents/')) {
          pathToDelete = docData.file_url.split('/storage/v1/object/public/documents/')[1];
        }
      }

      const { error: dbError } = await supabase.from('documents').delete().eq('id', documentId);
      if (dbError) throw dbError;

      if (pathToDelete) {
        await supabase.storage.from('documents').remove([pathToDelete]);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['dashboardStats', user?.id] });
    },
  });

  return {
    documents,
    loading,
    loadingMore,
    hasMore,
    error: error ? error.message : null,
    refreshDocuments,
    loadMoreDocuments,
    getDocument,
    deleteDocument: async (id, path) => deleteMutation.mutateAsync({ documentId: id, filePath: path }),
  };
};

export default useDocuments;